(function () {
  const DESKTOP_MIN = 990;
  const RESIZE_DEBOUNCE_MS = 120;

  const cleanupByRoot = new WeakMap();
  const mobileTimers = new WeakMap();
  const mobileObservers = new WeakMap();
  const mobileEndedHandlers = new WeakMap();

  function debounce(fn, wait) {
    let timeoutId = null;
    function debounced() {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        timeoutId = null;
        fn();
      }, wait);
    }
    debounced.cancel = function () {
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };
    return debounced;
  }

  function isDesktop() {
    return window.matchMedia(`(min-width: ${DESKTOP_MIN}px)`).matches;
  }

  function clearMobileVideoTimer(card) {
    const timerId = mobileTimers.get(card);
    if (timerId) {
      clearTimeout(timerId);
      mobileTimers.delete(card);
    }
  }

  function clearAllMobileVideoTimers(root) {
    root.querySelectorAll('[data-cst-card][data-cst-has-video="true"]').forEach((card) => {
      clearMobileVideoTimer(card);
    });
  }

  function disconnectMobileObservers(root) {
    root.querySelectorAll('[data-cst-card][data-cst-has-video="true"]').forEach((card) => {
      const observer = mobileObservers.get(card);
      if (observer) {
        observer.disconnect();
        mobileObservers.delete(card);
      }
      delete card.dataset.cstMobileBound;
    });
  }

  function stripMobileVideoClasses(root) {
    root.querySelectorAll("[data-cst-card]").forEach((card) => {
      card.classList.remove("is-mobile-video-playing", "is-mobile-video-ended");
    });
  }

  function endMobileVideo(card) {
    clearMobileVideoTimer(card);
    card.classList.remove("is-mobile-video-playing");
    card.classList.add("is-mobile-video-ended");
    const video = card.querySelector(".cst-card__video");
    if (video) {
      const onEnded = mobileEndedHandlers.get(card);
      if (onEnded) {
        video.removeEventListener("ended", onEnded);
        mobileEndedHandlers.delete(card);
      }
      video.loop = true;
      video.pause();
      try {
        video.currentTime = 0;
      } catch (e) {
        /* noop */
      }
    }
  }

  function resetMobileVideo(card) {
    clearMobileVideoTimer(card);
    card.classList.remove("is-mobile-video-playing", "is-mobile-video-ended");
    const video = card.querySelector(".cst-card__video");
    if (video) {
      const onEnded = mobileEndedHandlers.get(card);
      if (onEnded) {
        video.removeEventListener("ended", onEnded);
        mobileEndedHandlers.delete(card);
      }
      video.pause();
      try {
        video.currentTime = 0;
      } catch (e) {
        /* noop */
      }
    }
  }

  function startMobileVideo(card, durationMs) {
    if (isDesktop()) return;
    if (card.classList.contains("is-cst-quick-popup-open")) return;
    if (card.classList.contains("is-mobile-video-ended")) return;
    if (card.classList.contains("is-mobile-video-playing")) return;

    const video = card.querySelector(".cst-card__video");
    if (!video) return;

    card.classList.add("is-mobile-video-playing");
    video.loop = false;
    try {
      video.currentTime = 0;
    } catch (e) {
      /* noop */
    }

    const onEnded = () => {
      endMobileVideo(card);
    };
    const existingHandler = mobileEndedHandlers.get(card);
    if (existingHandler) {
      video.removeEventListener("ended", existingHandler);
    }
    video.addEventListener("ended", onEnded);
    mobileEndedHandlers.set(card, onEnded);

    video.play().catch(() => {});

    clearMobileVideoTimer(card);
    const timerId = setTimeout(() => {
      endMobileVideo(card);
    }, durationMs);
    mobileTimers.set(card, timerId);
  }

  function bindMobileCardMedia(card, durationMs) {
    if (card.dataset.cstMobileBound === "true") return;
    if (card.getAttribute("data-cst-has-video") !== "true") return;
    if (!card.querySelector(".cst-card__video")) return;
    if (typeof IntersectionObserver === "undefined") return;

    card.dataset.cstMobileBound = "true";

    /*
     * Root is the viewport (clipping by the horizontally scrolling slider is
     * still accounted for). Thresholds: dropping below 20% visible re-arms the
     * card so its video replays from 0:00 on re-entry; 50% visible starts
     * playback. The re-arm point is 20% rather than fully-out because the peek
     * layout can keep a sliver of an off-screen slide visible. The gap between
     * the two prevents reset/restart jitter at one boundary.
     */
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (isDesktop()) return;
          if (!entry.isIntersecting || entry.intersectionRatio < 0.2) {
            resetMobileVideo(card);
            return;
          }
          if (entry.intersectionRatio < 0.5) return;
          if (card.classList.contains("is-cst-quick-popup-open")) return;
          if (card.classList.contains("is-mobile-video-ended")) return;
          if (card.classList.contains("is-mobile-video-playing")) return;
          startMobileVideo(card, durationMs);
        });
      },
      {
        root: null,
        threshold: [0, 0.2, 0.5],
      }
    );

    observer.observe(card);
    mobileObservers.set(card, observer);
  }

  function getScrollStep(slider) {
    const slide = slider.querySelector(".cst-slide");
    if (!slide) return slider.clientWidth * 0.25;
    const styles = window.getComputedStyle(slider);
    const gap =
      parseFloat(styles.columnGap || styles.gap || "0") || 0;
    return slide.offsetWidth + gap;
  }

  function updateArrowDisabled(prevBtn, nextBtn, slider) {
    if (!slider || !prevBtn || !nextBtn) return;
    const maxScroll = slider.scrollWidth - slider.clientWidth;
    const left = slider.scrollLeft;
    const tol = 4;
    if (left <= tol) {
      prevBtn.setAttribute("disabled", "disabled");
    } else {
      prevBtn.removeAttribute("disabled");
    }
    if (maxScroll <= tol || left >= maxScroll - tol) {
      nextBtn.setAttribute("disabled", "disabled");
    } else {
      nextBtn.removeAttribute("disabled");
    }
  }

  /**
   * Pick the node with the largest horizontal scroll range (ul vs wrapper).
   */
  function getScrollableElement(node) {
    if (!node) return null;
    const panel = node.closest("[data-cst-panel]");
    let el = node;
    let best = node;
    let bestDelta = node.scrollWidth - node.clientWidth;
    for (let i = 0; i < 7; i += 1) {
      if (!el) break;
      if (panel && !panel.contains(el)) break;
      const d = el.scrollWidth - el.clientWidth;
      if (d > bestDelta) {
        bestDelta = d;
        best = el;
      }
      el = el.parentElement;
    }
    return best || node;
  }

  function getCstSlideCount(panel) {
    if (!panel) return 0;
    const sliderUl = panel.querySelector("[data-cst-slider]");
    return sliderUl ? sliderUl.querySelectorAll(".cst-slide").length : 0;
  }

  function setScrollFromTrackClientX(panel, clientX) {
    const u = panel.querySelector("[data-cst-slider]");
    if (!u) return;
    if (getCstSlideCount(panel) <= 1) return;
    const sc = getScrollableElement(u) || u;
    const maxScroll = sc.scrollWidth - sc.clientWidth;
    if (maxScroll <= 1) return;
    const track = panel.querySelector("[data-cst-progress-track]");
    if (!track) return;
    const rect = track.getBoundingClientRect();
    if (rect.width <= 0) return;
    const ratio = (clientX - rect.left) / rect.width;
    const clamped = Math.max(0, Math.min(1, ratio));
    sc.scrollTo({ left: clamped * maxScroll, behavior: "auto" });
  }

  function syncScrollableUi(scrollEl, layoutAttempt) {
    if (!scrollEl) return;
    const attempt = layoutAttempt || 0;
    const panel = scrollEl.closest("[data-cst-panel]");
    if (!panel || panel.hasAttribute("hidden")) {
      return;
    }
    /* The scroll area can report clientWidth 0 for a frame right after a tab is shown. */
    if (scrollEl.clientWidth < 2) {
      if (attempt < 3) {
        requestAnimationFrame(() => {
          const u = panel.querySelector("[data-cst-slider]");
          const sc2 = u ? getScrollableElement(u) || u : null;
          if (sc2) {
            syncScrollableUi(sc2, attempt + 1);
          }
        });
      }
      return;
    }
    const maxScroll = scrollEl.scrollWidth - scrollEl.clientWidth;
    const canScroll = getCstSlideCount(panel) > 1 && maxScroll > 1;
    const progress = panel.querySelector("[data-cst-progress]");
    if (progress) {
      progress.toggleAttribute("hidden", !canScroll);
    }
    const outer = panel.querySelector(".collection-slider-tabs__slider-outer");
    if (outer) {
      outer.classList.toggle("is-cst-scrollable", canScroll);
    }
  }

  function updateProgress(fillEl, scrollEl) {
    if (scrollEl) {
      syncScrollableUi(scrollEl);
    }
    if (!fillEl || !scrollEl) return;
    const total = scrollEl.scrollWidth;
    const view = scrollEl.clientWidth;
    const maxScroll = total - view;
    const sl = Math.max(0, Math.min(maxScroll, scrollEl.scrollLeft));
    if (maxScroll <= 1) {
      fillEl.style.width = "100%";
      fillEl.style.left = "0%";
      return;
    }
    const viewRatio = view / total;
    const thumbPercent = viewRatio * 100;
    const pos = sl / maxScroll;
    const travel = 100 - thumbPercent;
    fillEl.style.width = `${thumbPercent}%`;
    fillEl.style.left = `${pos * travel}%`;
  }

  function pauseVideosIn(root) {
    clearAllMobileVideoTimers(root);
    root.querySelectorAll(".cst-card__video").forEach((video) => {
      video.pause();
      try {
        video.currentTime = 0;
      } catch (e) {
        /* noop */
      }
    });
    root.querySelectorAll(".cst-card.is-media-hover").forEach((card) => {
      card.classList.remove("is-media-hover");
    });
    root.querySelectorAll(".cst-card.is-mobile-video-playing").forEach((card) => {
      card.classList.remove("is-mobile-video-playing");
      card.classList.add("is-mobile-video-ended");
    });
  }

  function closeCstQuickAddPopup(popup) {
    if (!popup || popup.getAttribute("data-cst-quick-add-popup") == null) return;
    popup.setAttribute("hidden", "");
    popup.classList.remove("is-open");
    const slide = popup.closest("[data-cst-slide]");
    const card = slide?.querySelector("[data-cst-card]");
    card?.classList.remove("is-cst-quick-popup-open");
    const opener = slide?.querySelector("[data-cst-quick-add-open]");
    opener?.setAttribute("aria-expanded", "false");
    if (card) {
      clearMobileVideoTimer(card);
      card.classList.remove("is-mobile-video-playing");
    }
    const video = card?.querySelector(".cst-card__video");
    if (video) {
      video.pause();
      try {
        video.currentTime = 0;
      } catch (e) {
        /* noop */
      }
    }
    card?.classList.remove("is-media-hover");
  }

  function closeAllCstQuickAddPopups() {
    document.querySelectorAll("[data-cst-quick-add-popup].is-open").forEach((p) => {
      closeCstQuickAddPopup(p);
    });
  }

  function openCstQuickAddPopup(popup, opener) {
    closeAllCstQuickAddPopups();
    popup.removeAttribute("hidden");
    popup.classList.add("is-open");
    const slide = popup.closest("[data-cst-slide]");
    const card = slide?.querySelector("[data-cst-card]");
    card?.classList.add("is-cst-quick-popup-open");
    if (card) {
      clearMobileVideoTimer(card);
      card.classList.remove("is-mobile-video-playing");
    }
    const video = card?.querySelector(".cst-card__video");
    if (video) {
      video.pause();
      try {
        video.currentTime = 0;
      } catch (e) {
        /* noop */
      }
    }
    card?.classList.remove("is-media-hover");
    if (opener) opener.setAttribute("aria-expanded", "true");
    const dialog = popup.querySelector("[role='dialog']");
    const firstFocus =
      dialog?.querySelector(".cst-quick-add-popup__atc:not([disabled])") ||
      dialog?.querySelector("[data-cst-quick-add-close]") ||
      dialog;
    requestAnimationFrame(() => {
      firstFocus?.focus({ preventScroll: true });
    });
  }

  let cstQuickAddEscapeBound = false;
  function bindCstQuickAddEscapeOnce() {
    if (cstQuickAddEscapeBound) return;
    cstQuickAddEscapeBound = true;
    document.addEventListener("keydown", (e) => {
      if (e.defaultPrevented) return;
      if (e.key !== "Escape") return;
      const open = document.querySelector("[data-cst-quick-add-popup].is-open");
      if (!open) return;
      e.preventDefault();
      const slide = open.closest("[data-cst-slide]");
      const opener = slide?.querySelector("[data-cst-quick-add-open]");
      closeCstQuickAddPopup(open);
      opener?.focus();
    });
  }

  function bindCardMedia(card) {
    if (card.dataset.cstMediaBound === "true") return;
    if (card.getAttribute("data-cst-has-video") !== "true") return;

    const video = card.querySelector(".cst-card__video");
    if (!video) return;

    card.dataset.cstMediaBound = "true";

    const enter = () => {
      if (!isDesktop()) return;
      if (card.classList.contains("is-cst-quick-popup-open")) return;
      card.classList.add("is-media-hover");
      video.play().catch(() => {});
    };

    const leave = () => {
      if (!isDesktop()) return;
      card.classList.remove("is-media-hover");
      video.pause();
      try {
        video.currentTime = 0;
      } catch (e) {
        /* noop */
      }
    };

    card.addEventListener("mouseenter", enter);
    card.addEventListener("mouseleave", leave);
    card.addEventListener("focusin", enter);
    card.addEventListener("focusout", leave);
  }

  function init(root) {
    if (!root || root.dataset.cstInitialized === "true") return;
    root.dataset.cstInitialized = "true";

    const arrowsWrap = root.querySelector("[data-cst-arrows]");
    const prevBtn = root.querySelector("[data-cst-prev]");
    const nextBtn = root.querySelector("[data-cst-next]");
    const tabs = root.querySelectorAll("[data-cst-tab]");
    const panels = root.querySelectorAll("[data-cst-panel]");

    let scrollListener = null;
    /** The element that receives scroll (ul or ancestor with the real overflow). */
    let activeScrollEl = null;
    let progressResizeObserver = null;

    function getActivePanel() {
      return root.querySelector("[data-cst-panel].is-active");
    }

    function getActiveSlider() {
      const panel = getActivePanel();
      return panel ? panel.querySelector("[data-cst-slider]") : null;
    }

    function getActiveProgressFill() {
      const panel = getActivePanel();
      return panel ? panel.querySelector("[data-cst-progress-fill]") : null;
    }

    function detachScroll() {
      if (activeScrollEl && scrollListener) {
        activeScrollEl.removeEventListener("scroll", scrollListener);
      }
      if (progressResizeObserver) {
        progressResizeObserver.disconnect();
        progressResizeObserver = null;
      }
      scrollListener = null;
      activeScrollEl = null;
    }

    function attachScroll(slider) {
      detachScroll();
      if (!slider) return;
      const scrollTarget = getScrollableElement(slider) || slider;
      activeScrollEl = scrollTarget;
      scrollListener = () => {
        const sc = getScrollableElement(getActiveSlider()) || getActiveSlider();
        updateProgress(getActiveProgressFill(), sc);
        updateArrowDisabled(prevBtn, nextBtn, sc);
      };
      activeScrollEl.addEventListener("scroll", scrollListener, { passive: true });
      if (typeof ResizeObserver === "function") {
        progressResizeObserver = new ResizeObserver(() => {
          if (activeScrollEl) scrollListener();
        });
        progressResizeObserver.observe(activeScrollEl);
        slider.querySelectorAll("img").forEach((img) => {
          if (!img.complete) {
            img.addEventListener("load", scrollListener, { once: true });
          }
        });
      }
      scrollListener();
    }

    function updateArrowsVisibility() {
      const panel = getActivePanel();
      if (!arrowsWrap || !panel) return;
      const show = panel.getAttribute("data-show-arrows") === "true";
      arrowsWrap.hidden = !show;
      if (show) {
        const u = getActiveSlider();
        updateArrowDisabled(
          prevBtn,
          nextBtn,
          u ? getScrollableElement(u) || u : null
        );
      }
    }

    function activatePanel(targetPanel) {
      if (!targetPanel) return;

      closeAllCstQuickAddPopups();
      pauseVideosIn(root);

      panels.forEach((p) => {
        const isActive = p === targetPanel;
        p.classList.toggle("is-active", isActive);
        if (isActive) {
          p.removeAttribute("hidden");
        } else {
          p.setAttribute("hidden", "");
        }
        const progressEl = p.querySelector("[data-cst-progress]");
        if (progressEl) {
          progressEl.setAttribute("hidden", "");
        }
        const outerEl = p.querySelector(".collection-slider-tabs__slider-outer");
        if (outerEl) {
          outerEl.classList.remove("is-cst-scrollable");
        }
        const slider = p.querySelector("[data-cst-slider]");
        if (slider && isActive) {
          const sc = getScrollableElement(slider) || slider;
          sc.scrollLeft = 0;
        }
      });

      tabs.forEach((tab) => {
        const id = tab.getAttribute("data-cst-panel-id");
        const match = targetPanel.id === id;
        tab.classList.toggle("is-active", match);
        tab.setAttribute("aria-selected", match ? "true" : "false");
        tab.setAttribute("tabindex", match ? "0" : "-1");
      });

      requestAnimationFrame(() => {
        const slider = getActiveSlider();
        attachScroll(slider);
        updateArrowsVisibility();
        requestAnimationFrame(() => {
          const u = getActiveSlider();
          const sc = u ? getScrollableElement(u) || u : null;
          updateProgress(getActiveProgressFill(), sc);
          updateArrowDisabled(prevBtn, nextBtn, sc);
        });
      });
    }

    tabs.forEach((tab) => {
      tab.addEventListener("click", () => {
        const id = tab.getAttribute("data-cst-panel-id");
        const panel = id ? document.getElementById(id) : null;
        if (panel && root.contains(panel)) {
          activatePanel(panel);
        }
      });
    });

    root.querySelectorAll("[data-cst-card]").forEach(bindCardMedia);

    const durationSec = parseInt(root.dataset.mobileVideoDuration, 10) || 5;
    const durationMs = durationSec * 1000;
    root
      .querySelectorAll('[data-cst-card][data-cst-has-video="true"]')
      .forEach((card) => {
        bindMobileCardMedia(card, durationMs);
      });

    bindCstQuickAddEscapeOnce();

    root.addEventListener("click", (e) => {
      const openBtn = e.target.closest("[data-cst-quick-add-open]");
      if (openBtn && root.contains(openBtn)) {
        e.preventDefault();
        const slide = openBtn.closest("[data-cst-slide]");
        const popup = slide?.querySelector("[data-cst-quick-add-popup]");
        if (!popup) return;
        if (popup.classList.contains("is-open")) {
          closeCstQuickAddPopup(popup);
          openBtn.focus();
        } else {
          openCstQuickAddPopup(popup, openBtn);
        }
        return;
      }
      const closeBtn = e.target.closest("[data-cst-quick-add-close]");
      if (closeBtn && root.contains(closeBtn)) {
        const popup = closeBtn.closest("[data-cst-quick-add-popup]");
        if (!popup || !root.contains(popup)) return;
        e.preventDefault();
        const slide = popup.closest("[data-cst-slide]");
        const opener = slide?.querySelector("[data-cst-quick-add-open]");
        closeCstQuickAddPopup(popup);
        opener?.focus();
      }
    });

    root.addEventListener("pointerdown", (e) => {
      if (e.button !== 0) return;
      const progressWrap = e.target.closest("[data-cst-progress]");
      if (!progressWrap || !root.contains(progressWrap)) return;
      const panel = progressWrap.closest("[data-cst-panel]");
      if (!panel || !panel.classList.contains("is-active")) return;
      const track = progressWrap.querySelector("[data-cst-progress-track]");
      if (!track) return;
      e.preventDefault();
      setScrollFromTrackClientX(panel, e.clientX);
      progressWrap.classList.add("is-scrubbing");
      if (track) track.classList.add("is-scrubbing");
      const onMove = (ev) => {
        setScrollFromTrackClientX(panel, ev.clientX);
      };
      const onUp = () => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onUp);
        progressWrap.classList.remove("is-scrubbing");
        if (track) track.classList.remove("is-scrubbing");
      };
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      window.addEventListener("pointercancel", onUp);
    });

    root.addEventListener("keydown", (e) => {
      if (!e.key || !e.target.closest) return;
      const track = e.target.closest("[data-cst-progress-track]");
      if (!track || !root.contains(track)) return;
      if (!["ArrowLeft", "ArrowRight", "Home", "End", "PageUp", "PageDown"].includes(e.key)) {
        return;
      }
      const panel = track.closest("[data-cst-panel]");
      if (!panel || !panel.classList.contains("is-active")) return;
      const u = panel.querySelector("[data-cst-slider]");
      if (!u) return;
      if (getCstSlideCount(panel) <= 1) return;
      const sc = getScrollableElement(u) || u;
      const maxScroll = sc.scrollWidth - sc.clientWidth;
      if (maxScroll <= 1) return;
      e.preventDefault();
      const step = getScrollStep(u);
      if (e.key === "Home") {
        sc.scrollTo({ left: 0, behavior: "smooth" });
      } else if (e.key === "End") {
        sc.scrollTo({ left: maxScroll, behavior: "smooth" });
      } else if (e.key === "ArrowLeft" || e.key === "PageUp") {
        sc.scrollBy({ left: -step, behavior: "smooth" });
      } else {
        sc.scrollBy({ left: step, behavior: "smooth" });
      }
    });

    if (prevBtn) {
      prevBtn.addEventListener("click", () => {
        const u = getActiveSlider();
        if (!u) return;
        const sc = getScrollableElement(u) || u;
        sc.scrollBy({
          left: -getScrollStep(u),
          behavior: "smooth",
        });
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener("click", () => {
        const u = getActiveSlider();
        if (!u) return;
        const sc = getScrollableElement(u) || u;
        sc.scrollBy({
          left: getScrollStep(u),
          behavior: "smooth",
        });
      });
    }

    const onWindowResize = () => {
      root.querySelectorAll("[data-cst-panel]").forEach((p) => {
        if (p.hasAttribute("hidden")) return;
        const u = p.querySelector("[data-cst-slider]");
        if (!u) return;
        const sc = getScrollableElement(u) || u;
        syncScrollableUi(sc);
      });
      const u = getActiveSlider();
      if (!u) return;
      const sc = getScrollableElement(u) || u;
      updateProgress(getActiveProgressFill(), sc);
      updateArrowDisabled(prevBtn, nextBtn, sc);
    };

    const debouncedResize = debounce(onWindowResize, RESIZE_DEBOUNCE_MS);
    window.addEventListener("resize", debouncedResize);

    const mq = window.matchMedia(`(min-width: ${DESKTOP_MIN}px)`);
    const onMqChange = () => {
      pauseVideosIn(root);
      if (isDesktop()) {
        stripMobileVideoClasses(root);
      }
      const u = getActiveSlider();
      if (u) {
        const sc = getScrollableElement(u) || u;
        updateProgress(getActiveProgressFill(), sc);
        updateArrowDisabled(prevBtn, nextBtn, sc);
      }
    };
    if (mq.addEventListener) {
      mq.addEventListener("change", onMqChange);
    } else if (mq.addListener) {
      mq.addListener(onMqChange);
    }

    function teardown() {
      root.querySelectorAll("[data-cst-quick-add-popup].is-open").forEach((p) => {
        closeCstQuickAddPopup(p);
      });
      clearAllMobileVideoTimers(root);
      disconnectMobileObservers(root);
      stripMobileVideoClasses(root);
      debouncedResize.cancel();
      window.removeEventListener("resize", debouncedResize);
      if (mq.removeEventListener) {
        mq.removeEventListener("change", onMqChange);
      } else if (mq.removeListener) {
        mq.removeListener(onMqChange);
      }
      detachScroll();
      delete root.dataset.cstInitialized;
      cleanupByRoot.delete(root);
    }
    cleanupByRoot.set(root, teardown);

    const firstPanel =
      root.querySelector("[data-cst-panel].is-active") ||
      root.querySelector("[data-cst-panel]");
    if (firstPanel) {
      activatePanel(firstPanel);
    } else {
      updateArrowsVisibility();
    }
  }

  function boot() {
    document.querySelectorAll("[data-cst-section]").forEach(init);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  document.addEventListener("shopify:section:load", (event) => {
    const root =
      event.target &&
      event.target.querySelector &&
      event.target.querySelector("[data-cst-section]");
    if (root) init(root);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    const container = event.target;
    if (!container || !container.querySelector) return;
    const root = container.querySelector("[data-cst-section]");
    if (!root) return;
    const teardown = cleanupByRoot.get(root);
    if (teardown) teardown();
  });

  /**
   * Initialize sliders injected into the DOM after page load (e.g. the
   * <product-recommendations> Tier 3 fallback, which sets innerHTML without
   * executing scripts so neither boot() nor shopify:section:load reach it).
   * init() is idempotent, so re-initializing already-bound sections is a no-op.
   */
  if (typeof MutationObserver !== "undefined") {
    const injectedObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (node.matches && node.matches("[data-cst-section]")) init(node);
          if (node.querySelectorAll) {
            node.querySelectorAll("[data-cst-section]").forEach(init);
          }
        });
      });
    });
    injectedObserver.observe(document.body, { childList: true, subtree: true });
  }
})();
